import { inject, Injectable, signal } from '@angular/core';
import { BannerType } from '@ndwnu/design-system';
import { switchMap, timer } from 'rxjs';
import { InfoMessageDto } from '@generated/backend-api/model/info-message-dto';
import { InfoMessageControllerService } from '@generated/backend-api/api/info-message-controller.service';
import { InfoMessageType } from '@generated/backend-api/model/info-message-type';

@Injectable({
  providedIn: 'root',
})
export class BannerService {
  private readonly infoMessageControllerService: InfoMessageControllerService = inject(
    InfoMessageControllerService,
  );
  private readonly INFO_MESSAGE_BANNER_ID_PREFIX: 'info-message-banner-' = 'info-message-banner-';

  readonly banners = signal<BannerSettings[]>([]);
  readonly infoMessageBanners = signal<InfoMessageBannerSettings[]>([]);

  constructor() {
    this.subscribeToInfoMessages();
  }

  addBanner(settings: BannerSettings): void {
    this.closeBanner(settings.id);
    this.banners.set([...this.banners(), settings].sort((a, b) => a.id.localeCompare(b.id)));
  }

  closeBanner(id: string): void {
    this.banners.set(this.banners().filter((banner) => banner.id !== id));
  }

  private subscribeToInfoMessages() {
    timer(0, 60 * 1000)
      .pipe(switchMap(() => this.infoMessageControllerService.getCurrent()))
      .subscribe((messages: InfoMessageDto[]): void => {
        messages.forEach((message: InfoMessageDto): void => {
          this.addInfoMessage({
            id: this.INFO_MESSAGE_BANNER_ID_PREFIX + message.id,
            title: this.getMessageTitle(message),
            message: message.message,
            type: message.type === InfoMessageType.DISRUPTION ? 'critical' : 'info',
          });
        });
      });
  }

  addInfoMessage(settings: InfoMessageBannerSettings) {
    const exists: boolean = this.infoMessageBanners().some(
      (b: BannerSettings): boolean => b.id === settings.id && !settings.deleted,
    );
    if (!exists) {
      this.infoMessageBanners.set(
        [...this.infoMessageBanners(), settings].sort(
          (a: BannerSettings, b: BannerSettings): number => a.id.localeCompare(b.id),
        ),
      );
    }
  }

  closeInfoMessage(id: string) {
    const updatedBanners = this.infoMessageBanners().map((banner): InfoMessageBannerSettings => {
      if (banner.id === id) {
        return { ...banner, deleted: true };
      }
      return banner;
    });
    this.infoMessageBanners.set(updatedBanners);
  }

  getMessageTitle(infoMessage: InfoMessageDto): string {
    switch (infoMessage.type) {
      case InfoMessageType.DISRUPTION:
        return 'Storing';
      case InfoMessageType.MAINTENANCE:
        return 'Gepland onderhoud';
      default:
        return 'Algemene mededeling';
    }
  }
}

export interface BannerSettings {
  id: string;
  title: string;
  message: string;
  type: BannerType;
}

export interface InfoMessageBannerSettings extends BannerSettings {
  deleted?: boolean;
}
